import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import * as SubscriptionActions from '../../redux/actions/SubscriptionActions'
import Header from '../../components/header/Header'
import { Footer } from '../../components/footer/Footer'
import SignUpModal from '../../pages/main/landing-page/SignUp'
import SignInModal from '../../pages/main/landing-page/SignIn'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import ProductsFeatures from './ProductsFeatures'
import OrderSummary from './OrderSummary'
import PlanSubscribeSteps from './PlanSubscribeSteps'
import ReviewConfirm from './ReviewConfirm'
import PricingTabs from './PricingTabs'
import PricingLegal from './PricingLegal'

const PLANS = [
  // {
  //   title: 'Free',
  //   subtitle: 'For newly minted creatives, or those on a small budget',
  //   priceYearly: 'Free',
  //   priceMonthly: 'Free',
  //   cummPrice: '',
  //   brief2: '+15-30% per licensing transaction',
  //   featuresTitle: 'Core features include',
  //   features: [
  //     '10 assets/month',
  //     'Licensing templates',
  //     'Provenance labelling',
  //     'Double encryption',
  //     'Creative Commons',
  //     'Visible watermarking',
  //     '"License Now" button',
  //     'Legally compliant e-signature',
  //     'Direct royalty payment',
  //   ],
  // },
  {
    title: 'Solo Creator',
    subtitle:
      'For individuals serious about licensing revenue and provenance labelling',
    priceYearly: '£4.50',
    priceMonthly: '£7.00',
    cummPrice: '£54',
    brief2: '+2% per licensing transaction if applicable',
    featuresTitle: 'Core features include',
    features: [
      '15 assets/month',
      'Bolt-on for large asset volume',
      'Licensing templates',
      'Provenance labelling',
      'Double encryption',
      'Creative Commons',
      'Visible watermarking',
      '"License Now" button',
      'Legally compliant e-signature',
      'Direct royalty payment',
    ],
  },
  {
    title: 'Business',
    subtitle:
      'Ideal for small teams requiring custom branding, licensing, and provenance labelling for large volumes of assets',
    priceYearly: '£20',
    priceMonthly: '£30',
    cummPrice: '£240',
    brief2: '+2% per licensing transaction if applicable',
    featuresTitle: 'Core features include',
    features: [
      '15 assets/month/user',
      'Bolt-on for large asset volume',
      'Licensing templates',
      'Provenance labelling',
      'Double encryption',
      'Creative Commons',
      'Visible watermarking',
      '"License Now" button',
      'Legally compliant e-signature',
      'Direct royalty payment',
      'Customised branding',
      'User permissions & roles',
      'Bulk agreements',
    ],
    popular: true,
  },
  {
    title: 'Business Pro',
    subtitle:
      'Professional organisations requiring custom branding, licensing, and provenance labelling for large volumes of assets',
    priceYearly: '£41.50',
    priceMonthly: '£60',
    cummPrice: '£498',
    brief2: '',
    featuresTitle: 'Core features include',
    features: [
      '20 assets/month/user',
      'Bolt-on for large asset volume',
      'Licensing templates',
      'Provenance labelling',
      'Double encryption',
      'Creative Commons',
      'Visible watermarking',
      '"License Now" button',
      'Legally compliant e-signature',
      'Direct royalty payment',
      'Customised branding',
      'User permissions & roles',
      'Bulk agreements',
      'No licensing fee',
    ],
  },
  {
    title: 'Enterprise',
    subtitle:
      'Large organisations requiring custom branding, licensing, and provenance labelling for large volumes of assets, Plus customer support.',
    priceYearly: '£500',
    priceMonthly: null,
    cummPrice: '£6000',
    brief2: 'Or Contact for a Custom Quote',
    featuresTitle: 'Core features include',
    features: [
      '50 assets/month/user',
      'Bolt-on for large asset volume',
      'Licensing templates',
      'Provenance labelling',
      'Double encryption',
      'Creative Commons',
      'Visible watermarking',
      '"License Now" button',
      'Legally compliant e-signature',
      'Direct royalty payment',
      'Customised branding',
      'User permissions & roles',
      'Bulk agreements',
      'No licensing fee',
      '24/7 support',
      'Single sign-on (SSO)',
      'Service level agreement',
      'Custom integration available',
    ],
  },
]

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PK)

const PricingPage = () => {
  const dispatch = useDispatch()
  const {
    subscriptions: { products, loading: loadingPricing },
    auth,
  } = useSelector((state) => state)
  const [isSignUpOpen, setIsSignUpOpen] = useState(false)
  const [isSignInOpen, setIsSignInOpen] = useState(false)
  const [pricingDurationActive, setPricingDurationActive] = useState(1)
  const [activeTab, setActiveTab] = useState(1)
  const [activeProduct, setActiveProduct] = useState(2)
  const [activePlanTab, setActivePlanTab] = useState(1)
  const [currentPlan, setCurrentPlan] = useState(null)
  const [registerFormData, setRegisterFormData] = useState(null)
  const [paymentTypeText] = useState({
    cycleOne: ' Annual Plan - yearly renewal',
    cycleTwo: ' Monthly Plan - monthly renewal',
  })
  const [billingMemberCount, setBillingMemberCount] = useState(0)

  const getProductPlan = (productCode) => {
    const myPlan = products.find((p) => p.code === productCode)
    setCurrentPlan(myPlan)
  }

  const loadUserSubscriptions = () => {
    dispatch(SubscriptionActions.currentUserSubscription())
  }

  const loadSubscriptionProducts = useCallback(() => {
    dispatch(SubscriptionActions.getAllSubscriptionProducts())
  }, [])

  const changeActivePlanTab = (value) => {
    setActivePlanTab(value)
  }

  const changeActiveProduct = (productCode) => {
    setActiveProduct(productCode)
    getProductPlan(productCode)
    setActivePlanTab(2)
  }

  useEffect(() => {
    loadSubscriptionProducts()
    if (auth?.isAuthenticated) {
      loadUserSubscriptions()
    }
  }, [loadSubscriptionProducts])

  return (
    <Elements stripe={stripePromise}>
      <Header
        setIsSignUpOpen={setIsSignUpOpen}
        setIsSignInOpen={setIsSignInOpen}
      />
      <div className="pricing">
        <div className="pricing-container">
          <div className="pricing-title">
            <h2>Choose your plan</h2>
          </div>
          {activePlanTab === 1 && (
            <PricingTabs
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              pricingDurationActive={pricingDurationActive}
              setPricingDurationActive={setPricingDurationActive}
            />
          )}
          {activeTab === 1 && (
            <div className="pricing-plans">
              {loadingPricing && (
                <div style={{ textAlign: 'center' }}>
                  <p>
                    <FontAwesomeIcon className="spinner" icon={faSpinner} />
                  </p>
                </div>
              )}
              {!loadingPricing && (
                <>
                  {activePlanTab !== 1 && (
                    <PlanSubscribeSteps
                      active={activePlanTab}
                      changeActive={changeActivePlanTab}
                    />
                  )}
                  <ProductsFeatures
                    products={products}
                    pricingDurationActive={pricingDurationActive}
                    changeActiveProduct={changeActiveProduct}
                    show={activePlanTab === 1}
                  />
                  <OrderSummary
                    activeCycle={pricingDurationActive}
                    changeActiveCycle={setPricingDurationActive}
                    activeProduct={activeProduct}
                    changeActive={changeActivePlanTab}
                    currentPlan={currentPlan}
                    show={activePlanTab === 2}
                    setRegisterFormData={setRegisterFormData}
                    setBillingMemberCount={setBillingMemberCount}
                    billingMemberCount={billingMemberCount}
                  />
                  <ReviewConfirm
                    activeCycle={pricingDurationActive}
                    paymentTypeText={paymentTypeText}
                    currentPlan={currentPlan}
                    show={activePlanTab === 3}
                    registerFormData={registerFormData}
                    billingMemberCount={billingMemberCount}
                  />
                </>
              )}
            </div>
          )}
          {activeTab === 2 && (
            <div className="pricing-api">
              <br />
              <p>
                A REST API is available to enterprise account holders for
                processing large volumes of digital content.
              </p>
              <p>
                For access details, technical documentation and account
                eligibility, contact your enterprise account holder.
              </p>
              <p>
                Custom integrations are also available, including configurable
                delivery destinations for processed assets and associated
                metadata. Ask your enterprise account holder to arrange a
                meeting with our team to discuss your requirements.
              </p>
              <br />
              <br />
              <br />
              <br />
              <br />
              <br />
              <br />
              <br />
              <br />
              <br />
              <br />
              <br />
              <br />
              <br />
              <br />
              <br />
            </div>
          )}
          {activeTab === 3 && <PricingLegal />}
        </div>
      </div>
      <br />
      <Footer />
      {isSignUpOpen && (
        <SignUpModal
          setIsSignUpOpen={setIsSignUpOpen}
          setIsSignInOpen={setIsSignInOpen}
        />
      )}
      {isSignInOpen && (
        <SignInModal
          setIsSignUpOpen={setIsSignUpOpen}
          setIsSignInOpen={setIsSignInOpen}
        />
      )}
    </Elements>
  )
}

export default PricingPage
